import { z } from "zod";
import type { ToolScope } from "./tools/define.js";

/**
 * 横断検索（#224、docs/90 段1）。ここ1か所が正本：
 *   - 種類（索引の kind）・種類ごとのスコープ（docs/87 §5.1）・画面の言葉（SEARCH_MSG・種類と状態の名前）
 *   - 入力中の検索 GET /search・単価履歴 GET /search/unit-prices・AI回答 POST /search/answer の形
 *   - 期間の解き方（LLM に日付を計算させない＝docs/90 §11.1 カ）
 *   - AI回答の札の差し込みと検査（fillAnswer。LLM に数字・ID を作らせない＝§6・§11.1 キ）
 * 索引は DB の search_documents（RLS・暗号化した列は入れない）。ツールの定義は ./tools/search.ts
 */

/** 段1 の種類（photo・file は段3） */
export const SEARCH_KINDS = ["partner", "project_group", "estimate", "line", "invoice", "receipt", "vendor_quote", "rfq", "order", "purchase", "received_document", "delivery", "delivery_note", "acceptance"] as const;
export type SearchKind = (typeof SEARCH_KINDS)[number];
export const SearchKind = z.enum(SEARCH_KINDS);

/** 種類ごとに要るスコープ（partners:read だけのトークンでは見積書が出ない。docs/90 §2.2・§10） */
export const SEARCH_KIND_SCOPE: Record<SearchKind, ToolScope> = {
  partner: "partners:read",
  project_group: "projects:read",
  estimate: "estimates:read",
  line: "estimates:read",
  rfq: "estimates:read",
  // #233：{order}は{vendor}への帳票（見積依頼と同じ）
  order: "estimates:read",
  // #233 3a：仕入（相手の請求）は請求書と同じ
  purchase: "invoices:read",
  invoice: "invoices:read",
  receipt: "invoices:read",
  vendor_quote: "vendor_quotes:read",
  received_document: "documents:read",
  delivery: "documents:read",
  // #266 1b：{deliveryNote}・{acceptance}は請求書と同じ
  delivery_note: "invoices:read",
  acceptance: "invoices:read",
};

/** 種類の名前（言葉のキーは画面で t() を通す） */
export const SEARCH_KIND_LABEL: Record<SearchKind, string> = {
  partner: "{customer}",
  project_group: "{project}",
  estimate: "見積書",
  line: "明細",
  invoice: "請求書",
  receipt: "領収書",
  vendor_quote: "{vendorQuote}",
  rfq: "見積依頼書",
  order: "{order}",
  purchase: "仕入",
  received_document: "受け取った書類",
  delivery: "送付状",
  delivery_note: "{deliveryNote}",
  acceptance: "{acceptance}",
};

/**
 * 取引先の種類の名前（#233 §13.7 ヘ）：取引先の索引の status は partner_type（client／vendor／both。使わないは archived＝種類が分からない）。
 * {vendor}だけなら{vendor}・両方なら「{customer}・{vendor}」・ほかは{customer}。ほかの種類は SEARCH_KIND_LABEL のまま（言葉のキーは画面・API が埋める）
 */
export function searchKindLabelOf(kind: SearchKind, status?: string | null): string {
  if (kind !== "partner") return SEARCH_KIND_LABEL[kind];
  return status === "vendor" ? "{vendor}" : status === "both" ? "{customer}・{vendor}" : "{customer}";
}
/** 段の見出し：取引先の段は当たった取引先の種類で（{vendor}だけ＝{vendor}・{customer}と{vendor}が混ざる・両方＝「{customer}・{vendor}」） */
export function searchGroupLabelOf(kind: SearchKind, statuses: ReadonlyArray<string | null | undefined>): string {
  if (kind !== "partner") return SEARCH_KIND_LABEL[kind];
  const types = [...new Set(statuses.filter((s): s is "client" | "vendor" | "both" => s === "client" || s === "vendor" || s === "both"))];
  if (types.length === 0) return SEARCH_KIND_LABEL.partner;
  return types.length === 1 ? searchKindLabelOf(kind, types[0]) : "{customer}・{vendor}";
}

/** 状態の名前（種類ごと。チップの文字＝色だけで伝えない）。無い状態はチップを出さない */
const STATUS_DOC: Record<string, string> = { draft: "下書き", issued: "発行済", sent: "発行済", paid: "入金済", ordered: "受注", lost: "失注", voided: "取消", unused: "不使用" };
const SEARCH_STATUS_LABEL: Record<SearchKind, Record<string, string>> = {
  partner: { client: "{customer}", vendor: "{vendor}", both: "{customer}・{vendor}", archived: "使わない" },
  project_group: { active: "進行中", completed: "完了" },
  estimate: STATUS_DOC,
  line: STATUS_DOC,
  invoice: STATUS_DOC,
  receipt: STATUS_DOC,
  vendor_quote: { pending: "AI読み取り中", processing: "AI読み取り中", done: "読み取り済み", failed: "読み取れません", rejected: "不採用" },
  rfq: { draft: "下書き", sent: "送付済", viewed: "送付済", answered: "回答あり", declined: "辞退", cancelled: "取消" },
  order: { draft: "下書き", issued: "発行済", accepted: "承諾済", voided: "取消" },
  purchase: { draft: "下書き", confirmed: "確定", voided: "取消" },
  received_document: { unsorted: "書類が未選択", order: "注文書", inquiry: "見積依頼", invoice: "請求書", drawing: "図面", photo: "写真", material: "資料", other: "その他" },
  delivery: { print: "印刷", download: "PDF", email: "メール", fax: "FAX" },
  delivery_note: { draft: "下書き", issued: "発行済", received: "受領済", voided: "取消" },
  acceptance: { draft: "下書き", issued: "未確認", accepted: "検収済", voided: "取消" },
};
export const searchStatusLabel = (kind: SearchKind, status: string | null | undefined): string => (status ? (SEARCH_STATUS_LABEL[kind][status] ?? "") : "");

/** 画面の文（docs/10 §5.1.2：名詞・説明は「？」・AI と名乗る） */
export const SEARCH_MSG = {
  label: "検索",
  open: "検索",
  close: "閉じる",
  aiTitle: "AI回答",
  aiHelp: "文で質問して Enter を押すと、AI が自社のデータから答えます。金額と件数は帳票の値です。答えの下の根拠から元の書類を開けます。",
  aiMaking: "AI回答作成中…",
  aiUnavailable: "AI回答は現在利用できません",
  aiFailed: "AI回答を作成できませんでした",
  aiLimitMinute: "AI回答の上限（1分 20 回）に達しました",
  aiLimitDay: "本日のAI回答の上限（300 回）に達しました",
  aiUsage: "使い方はヘルプを参照してください",
  aiUsageLink: "ヘルプ",
  evidence: "根拠",
  enterHint: "Enter で AI回答",
  empty: "該当なし",
  all: "すべて {n}件 ›",
  less: "先頭5件に戻す",
  detail: "詳細",
  pdf: "PDF",
  unitPrices: "単価履歴",
  unitPricesTitle: "「{name}」の単価履歴",
  unitPricesEmpty: "単価の記録がありません",
  unitPricesStats: "{n}件・最小 {min}・中央 {median}・最大 {max}",
  contactMatch: "担当者の氏名が一致",
  error: "検索できませんでした",
  /** 欄が空のときの質問例（AI回答のある会社だけ。docs/90 §13.5） */
  examples: "質問例",
  /** AI回答の頭の解釈の1行の札 */
  interpretation: "解釈",
  /** 0件の答え（API が決まった文で返す。{kind}＝種類の名前） */
  noneOf: "該当する{kind}はありません。",
  noneUnitPrice: "「{name}」の単価の記録はありません。",
} as const;

/** 入力中の検索の長さの上限・種類ごとの件数。minChars＝入力中の検索を引く文字数・debounceMs＝打ち終わりを待つ時間（docs/90 §13.5） */
export const SEARCH_LIMITS = { qMax: 100, answerQMax: 200, top: 5, expanded: 50, unitPriceItems: 50, findItems: 20, sumRows: 50, minChars: 2, debounceMs: 300 } as const;

/** GET /search?q=&kinds=&limit= */
export const SearchQuery = z.object({
  q: z.string().trim().min(1).max(SEARCH_LIMITS.qMax),
  kinds: z.array(SearchKind).max(SEARCH_KINDS.length).optional(),
  limit: z.union([z.literal(SEARCH_LIMITS.top), z.literal(SEARCH_LIMITS.expanded)]).optional(),
});
export type SearchQuery = z.infer<typeof SearchQuery>;

/** 結果の1行（題・番号・取引先と{project}・金額・日付・状態・画面の道）。個人情報は入らない（docs/90 §5） */
export const SearchHit = z.object({
  kind: SearchKind,
  id: z.string(),
  documentId: z.string().nullable(),
  title: z.string(),
  docNo: z.string(),
  context: z.string(),
  amount: z.number().int().nullable(),
  amountExTax: z.number().int().nullable(),
  date: z.string().nullable(),
  status: z.string().nullable(),
  url: z.string(),
  /** 担当者の氏名が完全一致した{customer}（氏名は出さない） */
  viaContact: z.boolean().optional(),
});
export type SearchHit = z.infer<typeof SearchHit>;
export const SearchGroup = z.object({ kind: SearchKind, total: z.number().int(), hits: z.array(SearchHit) });
export type SearchGroup = z.infer<typeof SearchGroup>;
export const SearchResult = z.object({ q: z.string(), groups: z.array(SearchGroup), tookMs: z.number() });
export type SearchResult = z.infer<typeof SearchResult>;

/** 期間（docs/90 §11.1 カ）：言葉か月（YYYY-MM）か、質問に書かれた日付（YYYY-MM-DD）。日付は API が日本時間で解く */
export const SEARCH_PERIODS = ["this_month", "last_month", "this_year", "last_year", "all"] as const;
export const SearchPeriod = z.enum(SEARCH_PERIODS);
export type SearchPeriod = z.infer<typeof SearchPeriod>;
const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
const isYmd = (s: string) => YMD_RE.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`)) && new Date(`${s}T00:00:00Z`).toISOString().startsWith(s);
export const SearchYmd = z.string().refine(isYmd, "日付は YYYY-MM-DD");
export const SearchMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "月は YYYY-MM");
export const PeriodInput = z.object({
  period: SearchPeriod.optional(),
  month: SearchMonth.optional(),
  from: SearchYmd.optional(),
  to: SearchYmd.optional(),
});
export type PeriodInput = z.infer<typeof PeriodInput>;

const pad = (n: number) => String(n).padStart(2, "0");
const lastDay = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/** 日本時間の今日（YYYY-MM-DD） */
export function todayTokyo(now: Date = new Date()): string {
  const t = new Date(now.getTime() + 9 * 3600_000);
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/** 期間を日付の範囲に（null は「全部」）。from／to を書いたときはそれが先、次に月、次に言葉。何も無ければ「全部」 */
export function resolvePeriod(p: PeriodInput, today: string): { from: string | null; to: string | null } {
  if (p.from || p.to) return { from: p.from ?? null, to: p.to ?? null };
  const [ty, tm] = today.split("-").map(Number) as [number, number];
  const month = (y: number, m: number) => ({ from: `${y}-${pad(m)}-01`, to: `${y}-${pad(m)}-${pad(lastDay(y, m))}` });
  if (p.month) {
    const [y, m] = p.month.split("-").map(Number) as [number, number];
    return month(y, m);
  }
  switch (p.period) {
    case "this_month":
      return month(ty, tm);
    case "last_month":
      return tm === 1 ? month(ty - 1, 12) : month(ty, tm - 1);
    case "this_year":
      return { from: `${ty}-01-01`, to: `${ty}-12-31` };
    case "last_year":
      return { from: `${ty - 1}-01-01`, to: `${ty - 1}-12-31` };
    default:
      return { from: null, to: null };
  }
}

/** 期間の見せ方（2026/10/01〜2026/10/31・全期間） */
export const periodText = (r: { from: string | null; to: string | null }): string =>
  r.from || r.to ? `${(r.from ?? "").replace(/-/g, "/")}〜${(r.to ?? "").replace(/-/g, "/")}` : "全期間";

// ---------------------------------------------------------------------------
// 単価履歴（GET /search/unit-prices?name=）
// ---------------------------------------------------------------------------
export const UnitPriceItem = z.object({
  documentId: z.string(),
  docNo: z.string(),
  subject: z.string(),
  partnerName: z.string(),
  date: z.string(),
  status: z.string(),
  quantity: z.number().nullable(),
  unit: z.string(),
  unitPrice: z.number().int().nullable(),
  costAmount: z.number().int().nullable(),
  url: z.string(),
});
export type UnitPriceItem = z.infer<typeof UnitPriceItem>;
export const UnitPriceHistory = z.object({
  name: z.string(),
  items: z.array(UnitPriceItem),
  /** DB が計算した最小・最大・中央値（単価のある行。LLM に計算させない） */
  stats: z.object({ count: z.number().int(), min: z.number().int().nullable(), max: z.number().int().nullable(), median: z.number().int().nullable() }),
});
export type UnitPriceHistory = z.infer<typeof UnitPriceHistory>;

// ---------------------------------------------------------------------------
// AI回答（POST /search/answer）
// ---------------------------------------------------------------------------
export const SearchAnswerRequest = z.object({
  q: z.string().trim().min(1).max(SEARCH_LIMITS.answerQMax),
  kinds: z.array(SearchKind).max(SEARCH_KINDS.length).optional(),
});
export type SearchAnswerRequest = z.infer<typeof SearchAnswerRequest>;
/** status は取引先の種類の名前のため（#233 §13.7 ヘ。ほかの種類は使わない） */
export const SearchEvidence = z.object({ kind: SearchKind, id: z.string(), title: z.string(), docNo: z.string(), url: z.string(), status: z.string().nullable().optional() });
export type SearchEvidence = z.infer<typeof SearchEvidence>;
/** off＝その会社は AI回答を使わない（運営が会社ごとに有効にする＝tenants.ai_answer_enabled。docs/90 §13）。AI を呼ばず索引の結果だけ */
export const SEARCH_ANSWER_STATUS = ["ok", "unavailable", "failed", "limit_minute", "limit_day", "off"] as const;
export const SearchAnswer = z.object({
  status: z.enum(SEARCH_ANSWER_STATUS),
  sentences: z.array(z.string()),
  evidence: z.array(SearchEvidence),
  /** 使い方の質問だった（答えの枠に「ヘルプ」へのリンク1行。docs/90 §7・★6） */
  usage: z.boolean(),
  /** 5 分のキャッシュの答え（GPU を使っていない） */
  cached: z.boolean(),
  /** 解釈の1行（ツールの引数から API が組み立てる＝「発行済の請求書・請求日が今月（…）で集計」。docs/90 §13.4）。呼んだツールごとに1つ */
  interpretation: z.array(z.string()).default([]),
  /** status が off のときだけ：索引の結果（入力中の検索と同じ。AI を呼ばない） */
  results: z.array(SearchGroup).optional(),
});
export type SearchAnswer = z.infer<typeof SearchAnswer>;

/** 答えの状態 → 画面の文（ok は答えそのもの） */
export const searchAnswerMessage = (status: SearchAnswer["status"]): string =>
  status === "unavailable" || status === "off" ? SEARCH_MSG.aiUnavailable : status === "limit_minute" ? SEARCH_MSG.aiLimitMinute : status === "limit_day" ? SEARCH_MSG.aiLimitDay : status === "failed" ? SEARCH_MSG.aiFailed : "";

/** LLM が返す答えの JSON（2回目の呼び出しの json_schema と同じ形） */
export const AnswerRaw = z.object({
  answerable: z.boolean(),
  usage: z.boolean().default(false),
  sentences: z.array(z.object({ text: z.string().max(300), refs: z.array(z.string().max(10)).max(20) })).max(5),
});
export type AnswerRaw = z.infer<typeof AnswerRaw>;

/** 2回目の呼び出しの json_schema（strict。vLLM の構造化出力） */
export const ANSWER_JSON_SCHEMA = {
  type: "object",
  properties: {
    answerable: { type: "boolean" },
    usage: { type: "boolean" },
    sentences: {
      type: "array",
      maxItems: 3,
      items: {
        type: "object",
        properties: { text: { type: "string", maxLength: 160 }, refs: { type: "array", maxItems: 10, items: { type: "string", maxLength: 8 } } },
        required: ["text", "refs"],
        additionalProperties: false,
      },
    },
  },
  required: ["answerable", "usage", "sentences"],
  additionalProperties: false,
} as const;

/** 札：数字は v1…、根拠の行は e1… */
const TOKEN_RE = /\{([ve]\d{1,3})\}/g;
const DIGITS_RE = /[0-9０-９][0-9０-９,，.．]*/g;
const halfDigits = (s: string) => s.replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0)).replace(/[,，]/g, "").replace(/．/g, ".");

export type FillFail = "not_answerable" | "unknown_ref" | "no_sentences" | "no_evidence";
export type FillResult = { ok: true; sentences: string[]; evidenceIds: string[] } | { ok: false; reason: FillFail };

/**
 * LLM の答えに札の値を差し込み、検査する（docs/90 §6「根拠の検査」・§11.1 キ）。
 *   - 文の中の {v1} は facts の値（API がツールの結果から作った文字＝金額・件数・率）、{e1} は items の題に置き換える
 *   - 結果に無い札（文の中・refs のどちらでも）が1つでもあれば答えを捨てる（unknown_ref）
 *   - 札でない数字は質問に書かれた数字だけ通す（ほかの数字のある文は落とす）
 *   - 残った文の refs の和が根拠。文が残らない・根拠が無いなら答えを出さない
 *   - fallbackEvidence（#224 の手直し、docs/90 §13.4）：AI が refs を付け忘れた（どの文も refs が空）ときだけ、数えたツールの結果の行の札（API が渡す・上位5件）を根拠にする。
 *     AI が作った札（結果に無い札）は今までどおり捨てる。本番で「返事待ちの見積は？」が正しい文なのに refs が空で捨てられた
 */
export function fillAnswer(raw: AnswerRaw, facts: Record<string, string>, items: Record<string, { title: string }>, question: string, fallbackEvidence: readonly string[] = []): FillResult {
  if (!raw.answerable) return { ok: false, reason: "not_answerable" };
  const known = (h: string) => (h.startsWith("v") ? h in facts : h in items);
  for (const s of raw.sentences) {
    for (const m of s.text.matchAll(TOKEN_RE)) if (!known(m[1]!)) return { ok: false, reason: "unknown_ref" };
    for (const r of s.refs) if (!(r in items)) return { ok: false, reason: "unknown_ref" };
  }
  const allowed = new Set([...question.matchAll(DIGITS_RE)].map((m) => halfDigits(m[0]).replace(/\.$/, "")));
  const sentences: string[] = [];
  const evidence: string[] = [];
  for (const s of raw.sentences.slice(0, 3)) {
    const bare = s.text.replace(TOKEN_RE, "");
    const nums = [...bare.matchAll(DIGITS_RE)].map((m) => halfDigits(m[0]).replace(/\.$/, ""));
    if (nums.some((n) => !allowed.has(n))) continue;
    const text = s.text
      .replace(TOKEN_RE, (_, h: string) => (h.startsWith("v") ? facts[h]! : items[h]!.title))
      .replace(/[{}]/g, "")
      .trim();
    if (!text) continue;
    sentences.push(text);
    for (const r of s.refs) if (!evidence.includes(r)) evidence.push(r);
  }
  if (sentences.length === 0) return { ok: false, reason: "no_sentences" };
  if (evidence.length === 0) for (const h of fallbackEvidence) if (h in items && !evidence.includes(h)) evidence.push(h);
  if (evidence.length === 0) return { ok: false, reason: "no_evidence" };
  return { ok: true, sentences, evidenceIds: evidence };
}

/** キャッシュの鍵にする質問の形（全角・半角と空白をそろえる） */
export const normalizeQuestion = (q: string): string => q.normalize("NFKC").replace(/\s+/g, " ").trim().toLowerCase();

/** 円の見せ方（3桁区切り＋円） */
export const yenText = (n: number): string => `${n.toLocaleString("ja-JP")}円`;
